import ReportList from '../../../components/ReportList';
import ReportToolBar from '../../../components/ReportToolBar';
import { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WorkflowStep } from './useWorkflow';
import { API_URL } from '../../../api';
import { useIntl } from 'react-intl';
import { DeleteButton } from '../../../components/DeleteButton/DeleteButton';
import { DeleteDialog } from '../../../components/DeleteDialog/DeleteDialog';
import { useAuthErrorContext } from '../../../components/AuthErrorProvider';
import { deletePreClientProposals } from '../../../api/api';
import { DefaultButton, Stack, ProgressIndicator } from '@fluentui/react';
import styles from './Main.module.scss';
import { ReportItem } from '../../../components/ReportItem/ReportItem';

function Main({
  setSelectedFiles,
  setIsJobDescriptionModalOpen,
  setCurrentStep,
  setSelectedProposal,
  orderByOptions,
  setOrderBy,
  orderBy,
}: {
  setSelectedFiles: (files: File[]) => void;
  setIsJobDescriptionModalOpen: (b: any) => void;
  setCurrentStep: (value: WorkflowStep) => void;
  setSelectedProposal: (proposal: any) => void;
  orderByOptions: any[];
  setOrderBy: (value: any) => void;
  orderBy: { property: string; desc: boolean };
}) {
  const intl = useIntl();
  const [checkedItems, setCheckedItems] = useState<any[]>([]);
  const [clientProposalReport, setClientProposalReport] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const { handleAuthError } = useAuthErrorContext();

  const handleDownload = async () => {
    const zip = new JSZip();
    const filesToDownload = checkedItems.length > 0 ? checkedItems : clientProposalReport;

    for (const file of filesToDownload) {
      const response = await fetch(file.fullpath);
      const data = await response.blob();

      const fileNameWithoutExt = file.filename.replace(/\.[^/.]+$/, '');
      zip.file(`${fileNameWithoutExt}_original${file.filename.match(/\.[^/.]+$/)[0]}`, data);

      if (file.status === 'Completed' && file.remarks) {
        try {
          const processedResponse = await fetch(file.remarks);
          const processedData = await processedResponse.blob();
          zip.file(`${fileNameWithoutExt}_processed${file.filename.match(/\.[^/.]+$/)[0]}`, processedData);
        } catch (error) {
          console.error('Error downloading processed file:', error);
        }
      }
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `clientProposals_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.zip`);
  };

  // Initial fetch
  useEffect(() => {
    const fetchClientProposalReports = async () => {
      setIsInitialLoading(true);
      try {
        const response = await fetch(API_URL + 'preclientproposals');
        const data = await response.json();
        setClientProposalReport(data);

        // Update checked items to maintain selection
        if (checkedItems.length > 0) {
          const updatedCheckedItems = checkedItems.filter(checkedItem =>
            data.some((item: any) => item.filename === checkedItem.filename),
          );
          if (updatedCheckedItems.length !== checkedItems.length) {
            setCheckedItems(updatedCheckedItems);
          }
        }
      } catch (error) {
        console.error('Error fetching reports:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchClientProposalReports();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
      setIsJobDescriptionModalOpen(true);
    } else {
      alert('Please select a file first!');
    }
  };

  const handleDelete = async () => {
    if (!checkedItems.length) {
      return;
    }
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const ids = checkedItems.map(item => item.filename);
      await deletePreClientProposals({ ids }, handleAuthError);

      const response = await fetch(API_URL + 'preclientproposals');
      const data = await response.json();
      setClientProposalReport(data);
      setCheckedItems([]);
    } catch (error) {
      console.error('Error deleting files:', error);
      alert('An error occurred while deleting files');
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <div className={styles.container}>
      <Stack horizontal className={styles.root}>
        <Stack horizontal tokens={{ childrenGap: 20 }} style={{ width: '100%' }}>
          <Stack.Item grow={1} style={{ height: '100%' }}>
            <div className={styles.mainContent}>
              {!clientProposalReport.length && !isInitialLoading && (
                <p className={styles.emptyStateMessage}>{intl.formatMessage({ id: 'clientProposal.empty.message' })}</p>
              )}

              <ReportToolBar orderByOptions={orderByOptions} setOrderBy={setOrderBy}>
                <input
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="file-upload"
                  multiple
                  accept=".docx,.pdf"
                />
                <DefaultButton
                  iconProps={{ iconName: 'Add' }}
                  text={intl.formatMessage({ id: 'clientProposal.button.startWorkflow' })}
                  onClick={() => {
                    setSelectedProposal(null);
                    setCurrentStep(WorkflowStep.Step1);
                  }}
                  className={styles.uploadButton}
                  htmlFor="file-upload"
                />

                <DeleteButton itemCount={checkedItems.length} onClick={handleDelete} />

                <DefaultButton
                  iconProps={{ iconName: 'Download' }}
                  text={intl.formatMessage(
                    { id: 'clientProposal.button.download' },
                    { count: checkedItems.length === 0 ? 'All' : checkedItems.length },
                  )}
                  onClick={handleDownload}
                  disabled={!clientProposalReport.length}
                  primary
                  className={styles.downloadButton}
                />
              </ReportToolBar>

              {isLoading || isInitialLoading ? (
                <div className={styles.progressContainer}>
                  <ProgressIndicator />
                </div>
              ) : (
                <div className={styles.progressContainer}>
                  <ProgressIndicator percentComplete={0} />
                </div>
              )}

              <DeleteDialog
                hidden={!isDeleteDialogOpen}
                onDismiss={() => setIsDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                itemCount={checkedItems.length}
                messageId="clientProposal.delete.confirmMessage"
                titleId="clientProposal.delete.confirmTitle"
              />

              <ReportList
                orderBy={orderBy}
                items={clientProposalReport}
                onChecked={checkedItems => {
                  setCheckedItems(checkedItems);
                }}
                onItemClick={item => {
                  const details = item.details || {};
                  const currentStep = parseInt(details.currentStep || '1', 10);

                  const fullpath = item.fullpath;
                  const encodedFilename = fullpath.split('/preclientproposalpd/')[1]?.split('?')[0];
                  const filename = encodedFilename ? decodeURIComponent(encodedFilename) : item.filename;

                  const proposal = {
                    filename,
                    fullpath: item.fullpath,
                    status: item.status || details.status || 'Pending',
                    details: {
                      ...details,
                      consultingTeam: details.consultingTeam || [],
                      currentStep: currentStep,
                      lastModified: details.lastModified || new Date().toISOString(),
                    },
                  };

                  if (details.step2Data) {
                    proposal.details = {
                      ...proposal.details,
                      ...details.step2Data,
                    };
                  }

                  if (details.step3Data) {
                    proposal.details = {
                      ...proposal.details,
                      similiarRoles: details.step3Data.similiarRoles || [],
                      similiarSectors: details.step3Data.similiarSectors || [],
                      similiarOrganisations: details.step3Data.similiarOrganisations || [],
                      similiarCustom: details.step3Data.similiarCustom,
                    };
                  }

                  setSelectedProposal(proposal);
                  if (currentStep) {
                    setCurrentStep(currentStep as WorkflowStep);
                  }
                }}
                renderContent={item => (
                  <ReportItem
                    key={`${item.filename}-${item.createddate || ''}`}
                    filename={item.filename}
                    status={item.status || item.details?.status || 'Pending'}
                    details={{
                      consultingTeam: item.details?.consultingTeam,
                      lastModified: item.details?.lastModified,
                      currentStep: parseInt(item.details?.currentStep || '1', 10),
                    }}
                  />
                )}
              />
            </div>
          </Stack.Item>
        </Stack>
      </Stack>
    </div>
  );
}

export default Main;
